import React, { useEffect, useState } from 'react';
import API from '../api';
import './CheckoutPage.css';
import InvoicePage from './InvoicePage';
import SearchIcon from './../assets/images/search-icon.png';

const CheckoutPage = ({ payload, onBack }) => {
  const { bookingId, contact: contactFromCalendar, selectedService, customerDetails } = payload;
  const contact = bookingId === '000000000000000000000000' ? customerDetails : contactFromCalendar;

  const [services, setServices] = useState([]);
  const [addedServices, setAddedServices] = useState([selectedService?._id]);

  const [miscItems, setMiscItems] = useState([]);

  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [productSearchResults, setProductSearchResults] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);

  const [serviceSearchTerm, setServiceSearchTerm] = useState('');
  const [serviceSearchResults, setServiceSearchResults] = useState([]);

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [createdInvoiceId, setCreatedInvoiceId] = useState(null);

  /* NEW: separate tax + tip controls */
  const [productTaxPercent, setProductTaxPercent] = useState(0);
  const [serviceTaxPercent, setServiceTaxPercent] = useState(0);

  const [showTipSection, setShowTipSection] = useState(false);
  const [tipMode, setTipMode] = useState('percent'); // 'percent' or 'amount'
  const [tipInput, setTipInput] = useState(''); // raw value from input

  const user = JSON.parse(localStorage.getItem('user'));
  const businessId = localStorage.getItem('id');
  const employeeId =
    user?.role === 'employee'
      ? user.id
      : user?.user?._id || user?.id;

  /* load services */
  useEffect(() => {
    API.get(`/auth/services/business/${businessId}`)
      .then((res) => setServices(res.data))
      .catch((err) =>
        console.error('Failed to load services', err)
      );
  }, [businessId]);

  /* product search */
  useEffect(() => {
    if (!productSearchTerm) {
      setProductSearchResults([]);
      return;
    }
    API.get(
      `/auth/products/search?query=${productSearchTerm}&businessId=${businessId}`
    )
      .then((res) => setProductSearchResults(res.data))
      .catch((err) =>
        console.error('Product search failed', err)
      );
  }, [productSearchTerm, businessId]);

  /* service search */
  useEffect(() => {
    if (!serviceSearchTerm) {
      setServiceSearchResults([]);
      return;
    }
    API.get(
      `/auth/services/search?query=${serviceSearchTerm}&businessId=${businessId}`
    )
      .then((res) => setServiceSearchResults(res.data))
      .catch((err) =>
        console.error('Service search failed', err)
      );
  }, [serviceSearchTerm, businessId]);

  /* misc helpers */
  const handleAddMiscItem = () =>
    setMiscItems([
      ...miscItems,
      { name: '', price: 0 },
    ]);

  const handleMiscItemChange = (index, key, val) => {
    const updated = [...miscItems];
    if (key === 'price') {
      const n = parseFloat(val);
      updated[index].price =
        Number.isFinite(n) && n >= 0 ? n : 0;
    } else {
      updated[index][key] = val;
    }
    setMiscItems(updated);
  };

  const removeMiscItem = (index) =>
    setMiscItems((prev) =>
      prev.filter((_, i) => i !== index)
    );

  /* product helpers */
  const handleAddProduct = (product) => {
    setSelectedProducts((prev) => {
      if (prev.find((p) => p._id === product._id)) return prev;
      return [...prev, { ...product, quantity: 1 }];
    });
  };

  const handleProductQuantityChange = (
    productId,
    quantity
  ) => {
    setSelectedProducts((prev) =>
      prev.map((p) =>
        p._id === productId
          ? {
              ...p,
              quantity:
                Math.max(
                  1,
                  parseInt(quantity, 10) || 1
                ),
            }
          : p
      )
    );
  };

  const handleRemoveProduct = (productId) =>
    setSelectedProducts((prev) =>
      prev.filter((p) => p._id !== productId)
    );

  /* calculations */
  const calculateServiceTotal = () =>
    addedServices.reduce((sum, id) => {
      const s = services.find((x) => x._id === id);
      return sum + (s?.price || 0);
    }, 0);

  const calculateProductTotal = () =>
    selectedProducts.reduce(
      (sum, p) =>
        sum +
        (p.price_per_unit || 0) *
          (p.quantity || 0),
      0
    );

  const calculateMiscTotal = () =>
    miscItems.reduce(
      (sum, item) => sum + (item.price || 0),
      0
    );

  const serviceTotal = calculateServiceTotal();
  const productTotal = calculateProductTotal();
  const miscTotal = calculateMiscTotal();

  const subTotal = serviceTotal + productTotal + miscTotal;

  const parsedServiceTax =
    parseFloat(serviceTaxPercent) || 0;
  const parsedProductTax =
    parseFloat(productTaxPercent) || 0;

  const serviceTax =
    serviceTotal * (parsedServiceTax / 100);
  const productTax =
    productTotal * (parsedProductTax / 100);
  const totalTax = serviceTax + productTax;

  let tip = 0;
  const parsedTip = parseFloat(tipInput) || 0;
  if (showTipSection && parsedTip > 0) {
    if (tipMode === 'percent') {
      tip = subTotal * (parsedTip / 100);
    } else {
      tip = parsedTip;
    }
  }

  const finalTotal = subTotal + totalTax + tip;

  /* contact helpers */
  const fetchOrCreateContact = async () => {
    try {
      const { phone, email, name } = contact || {};
      if (!name || !phone || !businessId) return null;

      const res = await API.get(
        `/auth/contacts/search`,
        { params: { businessId, name, phone } }
      );
      const existing = res.data?.[0];
      if (existing?._id) return existing._id;

      const createRes = await API.post(
        `/auth/contacts`,
        {
          name,
          phone,
          email,
          business_id: businessId,
        }
      );
      return createRes.data?._id || null;
    } catch (e) {
      console.error(e);
      return null;
    }
  };

  /* payment + invoice */
  const handleCashPayment = async () => {
    const isManual =
      bookingId ===
      '000000000000000000000000';

    if (!businessId || !bookingId) {
      alert('Missing required invoice fields.');
      return;
    }
    if (!addedServices?.length) {
      alert('Please select at least one service.');
      return;
    }

    let resolvedContactId = contact?._id;
    if (isManual && !resolvedContactId) {
      resolvedContactId =
        await fetchOrCreateContact();
      if (!resolvedContactId) {
        alert(
          'Failed to resolve customer contact.'
        );
        return;
      }
    }

    const productsForInvoice =
      selectedProducts.map((p) => ({
        productId: p._id,
        name: p.name,
        price_per_unit: p.price_per_unit,
        quantity: p.quantity,
        total:
          (p.price_per_unit || 0) *
          (p.quantity || 0),
      }));

    // effective overall tax percent (for compatibility)
    const effectiveTaxPercent =
      subTotal > 0
        ? (totalTax / subTotal) * 100
        : 0;

    const invoiceData = {
      bookingId: isManual
        ? '000000000000000000000000'
        : bookingId,
      businessId,
      employeeId: isManual
        ? undefined
        : employeeId,
      contactId: resolvedContactId,

      serviceIds: addedServices,
      serviceTotal,
      products: productsForInvoice,
      productsTotal: productTotal,
      miscItems,
      miscTotal,

      // NEW: detailed tax values
      productTaxPercent: parsedProductTax,
      serviceTaxPercent: parsedServiceTax,
      taxAmount: totalTax,

      // keep original field for backend compatibility
      taxPercent: effectiveTaxPercent,

      // tip handling
      tipAmount: tip,

      paymentMethod: 'cash',
      status: 'Paid',
      createdAt: new Date(),
    };

    try {
      const res = await API.post(
        '/auth/createInvoice',
        invoiceData
      );
      const invoiceId =
        res.data?.invoice?._id ||
        res.data?._id;
      if (invoiceId) {
        setCreatedInvoiceId(invoiceId);
      } else {
        alert(
          'Invoice created but ID not returned.'
        );
      }
    } catch (err) {
      console.error(
        'Error creating invoice',
        err
      );
      alert('Failed to create invoice.');
    }
  };

  /* show invoice once created */
  if (createdInvoiceId) {
    return (
      <InvoicePage
        createdInvoiceId={createdInvoiceId}
        selectedService={services.find(
          (s) =>
            s._id === addedServices[0]
        )}
      />
    );
  }

  /* icons */
  const Trash = () => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
    >
      <path d="M9 3h6a1 1 0 0 1 1 1v1h5v2h-2v13a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V7H3V5h5V4a1 1 0 0 1 1-1zm8 4H7v13h10V7zM9 9h2v9H9V9zm4 0h2v9h-2V9z" />
    </svg>
  );
  const Search = () => (
    <img
      className="sicon"
      src={SearchIcon}
      alt="Search"
    />
  );

  /* render */
  return (
    <div className="checkout-wrap">
      <button
        onClick={onBack}
        className="btn btn-ghost"
        style={{ marginBottom: 12 }}
      >
        ← Back to Calendar
      </button>
      <div className="checkout-title">
        Checkout
      </div>

      <div className="checkout-grid">
        {/* LEFT */}
        <div>
          {/* Services */}
          <div className="card">
            <div className="card-h">
              Add Services
            </div>
            <div className="card-body">
              <div className="search">
                <input
                  type="text"
                  placeholder="Search for a service"
                  value={serviceSearchTerm}
                  onChange={(e) =>
                    setServiceSearchTerm(
                      e.target.value
                    )
                  }
                />
                <Search />
              </div>

              {serviceSearchResults.length >
                0 && (
                <div className="dropdown">
                  {serviceSearchResults.map(
                    (s) => (
                      <div
                        key={s._id}
                        className="dropdown-item"
                        onClick={() => {
                          if (
                            !addedServices.includes(
                              s._id
                            )
                          ) {
                            setAddedServices(
                              (prev) => [
                                ...prev,
                                s._id,
                              ]
                            );
                            setServices(
                              (prev) =>
                                prev.find(
                                  (x) =>
                                    x._id ===
                                    s._id
                                )
                                  ? prev
                                  : [
                                      ...prev,
                                      s,
                                    ]
                            );
                          }
                          setServiceSearchTerm(
                            ''
                          );
                          setServiceSearchResults(
                            []
                          );
                        }}
                      >
                        <span className="row-left">
                          <span className="ellipsis">
                            {s.name}
                          </span>
                        </span>
                        <span className="price">
                          ${s.price}
                        </span>
                      </div>
                    )
                  )}
                </div>
              )}

              <div
                className="badge-gray"
                style={{
                  marginTop: 10,
                  marginBottom: 6,
                }}
              >
                Selected Services
              </div>
              {addedServices.map((id) => {
                const svc =
                  services.find(
                    (s) => s._id === id
                  );
                if (!svc) return null;
                return (
                  <div
                    key={id}
                    className="row"
                  >
                    <div className="row-left">
                      <div className="ellipsis">
                        {svc.name}
                      </div>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems:
                          'center',
                        gap: 8,
                      }}
                    >
                      <div className="price">
                        ${svc.price}
                      </div>
                      <button
                        className="icon-btn danger"
                        aria-label="Remove"
                        onClick={() =>
                          setAddedServices(
                            (
                              prev
                            ) =>
                              prev.filter(
                                (
                                  sid
                                ) =>
                                  sid !==
                                  id
                              )
                          )
                        }
                      >
                        <Trash />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Products */}
          <div className="card">
            <div className="card-h">
              Add Products
            </div>
            <div className="card-body">
              <div className="search">
                <input
                  type="text"
                  placeholder="Search for a product"
                  value={productSearchTerm}
                  onChange={(e) =>
                    setProductSearchTerm(
                      e.target.value
                    )
                  }
                />
                <Search />
              </div>

              {productSearchResults.length >
                0 && (
                <div className="dropdown">
                  {productSearchResults.map(
                    (p) => (
                      <div
                        key={p._id}
                        className="dropdown-item"
                        onClick={() => {
                          handleAddProduct(
                            p
                          );
                          setProductSearchTerm(
                            ''
                          );
                          setProductSearchResults(
                            []
                          );
                        }}
                      >
                        <span className="ellipsis">
                          {p.name}
                        </span>
                        <span className="price">
                          $
                          {
                            p.price_per_unit
                          }
                        </span>
                      </div>
                    )
                  )}
                </div>
              )}

              <div
                className="badge-gray"
                style={{
                  marginTop: 10,
                  marginBottom: 6,
                }}
              >
                Selected Products
              </div>
              {selectedProducts.map(
                (p) => (
                  <div
                    key={p._id}
                    className="row"
                  >
                    <div className="row-left">
                      <div className="ellipsis">
                        {p.name}
                      </div>
                      <div className="qty">
                        <button
                          type="button"
                          onClick={() =>
                            handleProductQuantityChange(
                              p._id,
                              p.quantity -
                                1
                            )
                          }
                        >
                          -
                        </button>
                        <input
                          type="number"
                          value={
                            p.quantity
                          }
                          min="1"
                          onChange={(
                            e
                          ) =>
                            handleProductQuantityChange(
                              p._id,
                              e
                                .target
                                .value
                            )
                          }
                          className="input-sm"
                          style={{
                            width: 64,
                            height: 30,
                          }}
                        />
                        <button
                          type="button"
                          onClick={() =>
                            handleProductQuantityChange(
                              p._id,
                              p.quantity +
                                1
                            )
                          }
                        >
                          +
                        </button>
                      </div>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems:
                          'center',
                        gap: 8,
                      }}
                    >
                      <div className="price">
                        $
                        {p.price_per_unit.toFixed(
                          2
                        )}
                      </div>
                      <button
                        className="icon-btn danger"
                        onClick={() =>
                          handleRemoveProduct(
                            p._id
                          )
                        }
                        aria-label="Remove"
                      >
                        <Trash />
                      </button>
                    </div>
                  </div>
                )
              )}
            </div>
          </div>

          {/* Misc */}
          <div className="card">
            <div className="card-h">
              Misc Items
              <div
                style={{
                  display: 'flex',
                  justifyContent:
                    'flex-end',
                  marginBottom: 8,
                }}
              >
                <button
                  className="btn btn-light"
                  type="button"
                  onClick={
                    handleAddMiscItem
                  }
                >
                  ＋ Add Item
                </button>
              </div>
            </div>

            <div className="card-body">
              <div className="table-head">
                <div>Item</div>
                <div>Price</div>
                <div>Action</div>
              </div>

              {miscItems.map(
                (item, i) => (
                  <div
                    key={i}
                    className="row-grid"
                  >
                    <div>
                      <input
                        className="input-sm"
                        placeholder="Item name"
                        value={
                          item.name
                        }
                        onChange={(e) =>
                          handleMiscItemChange(
                            i,
                            'name',
                            e
                              .target
                              .value
                          )
                        }
                      />
                    </div>
                    <div>
                      <input
                        className="input-sm"
                        type="number"
                        min="0"
                        placeholder="0"
                        value={
                          item.price
                        }
                        onChange={(e) =>
                          handleMiscItemChange(
                            i,
                            'price',
                            e
                              .target
                              .value
                          )
                        }
                      />
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent:
                          'flex-end',
                      }}
                    >
                      <button
                        className="icon-btn danger"
                        type="button"
                        onClick={() =>
                          removeMiscItem(
                            i
                          )
                        }
                        aria-label="Remove"
                      >
                        <Trash />
                      </button>
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        </div>

        {/* RIGHT — SUMMARY */}
        <div className="card summary-card">
          <div className="card-h">
            Summary
          </div>
          <div className="card-body">
            {/* Taxes */}
            <div className="summary-grid">
              <div>
                <div
                  className="badge-gray"
                  style={{
                    marginBottom: 6,
                  }}
                >
                  Tax on Products (%)
                </div>
                <input
                  className="input-sm"
                  type="number"
                  min="0"
                  value={
                    productTaxPercent
                  }
                  onChange={(e) =>
                    setProductTaxPercent(
                      e.target.value
                    )
                  }
                />
              </div>
              <div>
                <div
                  className="badge-gray"
                  style={{
                    marginBottom: 6,
                  }}
                >
                  Tax on Services (%)
                </div>
                <input
                  className="input-sm"
                  type="number"
                  min="0"
                  value={
                    serviceTaxPercent
                  }
                  onChange={(e) =>
                    setServiceTaxPercent(
                      e.target.value
                    )
                  }
                />
              </div>
            </div>

            {/* Tip (below tax) */}
            {!showTipSection && (
              <div style={{ marginTop: 8 }}>
                <button
                  type="button"
                  className="btn btn-light"
                  style={{
                    width: '100%',
                  }}
                  onClick={() =>
                    setShowTipSection(
                      true
                    )
                  }
                >
                  + Add Tip
                </button>
              </div>
            )}

            {showTipSection && (
              <div className="tip-section">
                <div className="tip-section-label">
                  Tip will be applied on
                  Subtotal
                  {` ($${subTotal.toFixed(
                    2
                  )})`}
                </div>
                <div className="tip-toggle-group">
                  <button
                    type="button"
                    className={
                      'tip-toggle-btn ' +
                      (tipMode ===
                      'percent'
                        ? 'active'
                        : '')
                    }
                    onClick={() => {
                      setTipMode(
                        'percent'
                      );
                      setTipInput('');
                    }}
                  >
                    %
                  </button>
                  <button
                    type="button"
                    className={
                      'tip-toggle-btn ' +
                      (tipMode ===
                      'amount'
                        ? 'active'
                        : '')
                    }
                    onClick={() => {
                      setTipMode(
                        'amount'
                      );
                      setTipInput('');
                    }}
                  >
                    $
                  </button>
                </div>
                <div className="tip-input-wrap">
                  {tipMode ===
                  'percent' ? (
                    <input
                      className="input-sm"
                      type="number"
                      min="0"
                      placeholder="Enter tip %"
                      value={
                        tipInput
                      }
                      onChange={(
                        e
                      ) =>
                        setTipInput(
                          e
                            .target
                            .value
                        )
                      }
                    />
                  ) : (
                    <input
                      className="input-sm"
                      type="number"
                      min="0"
                      placeholder="Enter tip amount"
                      value={
                        tipInput
                      }
                      onChange={(
                        e
                      ) =>
                        setTipInput(
                          e
                            .target
                            .value
                        )
                      }
                    />
                  )}
                </div>
                <div
                  className="badge-gray"
                  style={{
                    marginTop: 4,
                  }}
                >
                  Calculated Tip:{' '}
                  <strong>
                    $
                    {tip.toFixed(
                      2
                    )}
                  </strong>
                </div>
              </div>
            )}

            {/* Totals List */}
            <div className="kv">
              <span className="k">
                Services Total
              </span>
              <span className="v">
                $
                {serviceTotal.toFixed(
                  2
                )}
              </span>
            </div>
            <div className="kv">
              <span className="k">
                Products Total
              </span>
              <span className="v">
                $
                {productTotal.toFixed(
                  2
                )}
              </span>
            </div>
            <div className="kv">
              <span className="k">
                Misc Total
              </span>
              <span className="v">
                $
                {miscTotal.toFixed(
                  2
                )}
              </span>
            </div>
            <div className="kv">
              <span className="k">
                Subtotal
              </span>
              <span className="v">
                $
                {subTotal.toFixed(
                  2
                )}
              </span>
            </div>
            <div className="kv">
              <span className="k">
                Tax on Products (
                {
                  parsedProductTax
                }
                %)
              </span>
              <span className="v">
                $
                {productTax.toFixed(
                  2
                )}
              </span>
            </div>
            <div className="kv">
              <span className="k">
                Tax on Services (
                {
                  parsedServiceTax
                }
                %)
              </span>
              <span className="v">
                $
                {serviceTax.toFixed(
                  2
                )}
              </span>
            </div>
            <div className="kv">
              <span className="k">
                Tip
              </span>
              <span className="v">
                $
                {tip.toFixed(
                  2
                )}
              </span>
            </div>

            <div className="total-row">
              <span>
                Final Total
              </span>
              <span>
                $
                {finalTotal.toFixed(
                  2
                )}
              </span>
            </div>

            <div
              style={{
                marginTop: 16,
              }}
            >
              <button
                className="btn btn-primary"
                style={{
                  width: '100%',
                }}
                onClick={() =>
                  setShowPaymentModal(
                    true
                  )
                }
              >
                Continue to Payment
              </button>
            </div>
          </div>
        </div>
      </div>

      {showPaymentModal && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
        >
          <div className="modal">
            <h3>
              Select Payment
              Method
            </h3>
            <div className="modal-actions">
              <button
                className="btn btn-primary"
                onClick={
                  handleCashPayment
                }
              >
                Pay by Cash
              </button>
              <button
                className="btn btn-light"
                disabled
              >
                Pay by Card
                (Coming Soon)
              </button>
              <button
                className="btn btn-ghost"
                onClick={() =>
                  setShowPaymentModal(
                    false
                  )
                }
              >
                Back
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckoutPage;
